/**
 * NavMain — sidebar navigation renderer (issue #7).
 *
 * Adapted from standalone-search's NavMain.tsx.
 * Simplification: the donor used a hover-capability check (`useCanHover`) to
 * switch between an expanded-collapsible renderer and a dropdown renderer.
 * The LMS sidebar.tsx does not export `useCanHover`, so we always use the
 * expanded renderer (Collapsible + sub-items). This is correct for the
 * hover-capable desktop majority; mobile is already handled by the Sheet
 * wrapper in sidebar.tsx.
 */
import { Fragment } from "react";
import { Link, useLocation } from "react-router";

import { ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import { type NavGroup, type NavMainItem } from "@/navigation/sidebar-items";
import { TeachingNavigation } from "./TeachingNavigation";
import { LearningNavigation } from "./LearningNavigation";
import { RAIL_ITEM } from "./rail-item";

interface NavMainProps {
  readonly items: readonly NavGroup[];
  /** Route-contextual content injected beneath the matching nav item. */
  readonly contextualContent?: React.ReactNode;
  readonly contextualItemUrl?: string;
}

/** Seat groups render their own course collections and never absorb another. */
const SEAT_GROUPS = new Set(["admin-teaching", "instructor-main", "student-main"]);

const IsComingSoon = () => (
  <Badge variant="secondary" className="ms-auto">Soon</Badge>
);

function NavItem({
  item,
  isActive,
  isSubmenuOpen,
}: {
  item: NavMainItem;
  isActive: (item: NavMainItem) => boolean;
  isSubmenuOpen: (subItems?: NavMainItem["subItems"]) => boolean;
}) {
  return (
    <Collapsible
      asChild
      defaultOpen={isSubmenuOpen(item.subItems)}
      className="group/collapsible"
    >
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          {item.subItems ? (
            <SidebarMenuButton
              disabled={item.comingSoon}
              isActive={isActive(item)}
              tooltip={item.title}
              className={RAIL_ITEM}
            >
              <item.icon strokeWidth={1.75} aria-hidden="true" />
              <span>{item.title}</span>
              {item.comingSoon && <IsComingSoon />}
              <ChevronRight
                strokeWidth={1.75}
                aria-hidden="true"
                className="ms-auto size-4 transition-transform duration-[var(--motion-fast)] ease-[var(--ease-standard)] motion-reduce:transition-none group-data-[state=open]/collapsible:rotate-90"
              />
            </SidebarMenuButton>
          ) : (
            <SidebarMenuButton
              asChild
              aria-disabled={item.comingSoon}
              isActive={isActive(item)}
              tooltip={item.title}
              className={RAIL_ITEM}
            >
              <Link to={item.url} target={item.newTab ? "_blank" : undefined}>
                <item.icon strokeWidth={1.75} aria-hidden="true" />
                <span>{item.title}</span>
                {item.comingSoon && <IsComingSoon />}
              </Link>
            </SidebarMenuButton>
          )}
        </CollapsibleTrigger>
        {item.subItems && (
          <CollapsibleContent>
            <SidebarMenuSub>
              {item.subItems.map((subItem) => (
                <SidebarMenuSubItem key={subItem.title}>
                  <SidebarMenuSubButton
                    aria-disabled={subItem.comingSoon}
                    isActive={isActive(subItem)}
                    asChild
                    className={RAIL_ITEM}
                  >
                    <Link
                      to={subItem.url}
                      target={subItem.newTab ? "_blank" : undefined}
                    >
                      <subItem.icon strokeWidth={1.75} aria-hidden="true" />
                      <span>{subItem.title}</span>
                      {subItem.comingSoon && <IsComingSoon />}
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              ))}
            </SidebarMenuSub>
          </CollapsibleContent>
        )}
      </SidebarMenuItem>
    </Collapsible>
  );
}

export function NavMain({
  items,
  contextualContent,
  contextualItemUrl,
}: NavMainProps) {
  const location = useLocation();

  const isItemActive = (item: NavMainItem) => {
    if (item.subItems?.length) {
      return item.subItems.some((sub) => location.pathname.startsWith(sub.url));
    }
    return location.pathname === item.url
      || (item.activeOnDescendants === true && location.pathname.startsWith(`${item.url}/`));
  };

  const isSubmenuOpen = (subItems?: NavMainItem["subItems"]) =>
    subItems?.some((sub) => location.pathname.startsWith(sub.url)) ?? false;

  // An unlabelled group continues the section above it: it renders inside
  // that section rather than opening an unnamed one of its own. Navigation
  // data keeps the groups apart; only the rendering joins them.
  const sections = items.reduce<NavGroup[]>((joined, group) => {
    const previous = joined.at(-1);
    if (!group.label && previous && !SEAT_GROUPS.has(previous.id)) {
      joined[joined.length - 1] = { ...previous, items: [...previous.items, ...group.items] };
    } else {
      joined.push(group);
    }
    return joined;
  }, []);

  return (
    <>
      {sections.map((group) => group.id === "admin-teaching" || group.id === "instructor-main" ? (
        <TeachingNavigation key={group.id} group={group} />
      ) : group.id === "student-main" ? (
        <LearningNavigation key={group.id} group={group} />
      ) : (
        <SidebarGroup key={group.id}>
          {group.label && <SidebarGroupLabel className="eyebrow">{group.label}</SidebarGroupLabel>}
          <SidebarGroupContent className="flex flex-col gap-2">
            <SidebarMenu>
              {group.items.map((item) => {
                const node = (
                  <NavItem
                    key={item.title}
                    item={item}
                    isActive={isItemActive}
                    isSubmenuOpen={isSubmenuOpen}
                  />
                );

                if (!contextualContent || item.url !== contextualItemUrl) {
                  return node;
                }
                return (
                  <Fragment key={item.title}>
                    {node}
                    <li className="group-data-[collapsible=icon]:hidden">
                      {contextualContent}
                    </li>
                  </Fragment>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      ))}
    </>
  );
}
